import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'

import { AuthChrome } from '../../components/AuthChrome'
import { Button, Field, Notice } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'

export function VerifyEmail() {
  const { confirmSignUp, resendSignUpCode, authError } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  // Carried from sign-up, but editable: someone who reloads this page or opens
  // it from a bookmark has no query string and would otherwise be stuck.
  const [email, setEmail] = useState(params.get('email') ?? '')
  const [code, setCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const [resent, setResent] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !code.trim()) {
      setLocalError('Enter your email and the code we sent you.')
      return
    }
    setLocalError(null)
    setIsSubmitting(true)
    try {
      await confirmSignUp(email, code)
      navigate('/sign-in', { replace: true })
    } catch {
      // authError carries it.
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResend = async () => {
    setLocalError(null)
    setResent(false)
    try {
      await resendSignUpCode(email)
      setResent(true)
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'We could not send another code.')
    }
  }

  return (
    <AuthChrome
      eyebrow="One more step"
      title="Verify your email"
      lead="We sent a six-digit code. Enter it to finish creating your account."
      footer={
        <span>
          Wrong address? <Link className="auth__link" to="/sign-up">Start again</Link>
        </span>
      }
    >
      <form className="auth__form" onSubmit={handleSubmit} noValidate>
        {(localError || authError) && <Notice tone="error">{localError ?? authError}</Notice>}
        {resent && <Notice tone="info">A new code is on its way.</Notice>}

        <Field
          label="Email"
          type="email"
          value={email}
          autoComplete="username"
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label="Verification code"
          code
          value={code}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus={!!email}
          maxLength={6}
          onChange={(e) => setCode(e.target.value)}
        />

        <Button type="submit" full busy={isSubmitting}>
          {isSubmitting ? 'Verifying' : 'Verify email'}
        </Button>
        <Button type="button" variant="ghost" full onClick={() => void handleResend()}>
          Send another code
        </Button>
      </form>
    </AuthChrome>
  )
}
